import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'

/**
 * ScreenReaderAnnouncer Component
 * Based on SHARED-ACCESSIBILITY.md - ARIA Live Regions
 *
 * Announces dynamic content changes to screen readers.
 *
 * Politeness levels:
 * - polite: Waits for user to stop interacting
 * - assertive: Interrupts immediately (use sparingly)
 */

type Politeness = 'polite' | 'assertive'

interface AnnounceContextValue {
  announce: (message: string, politeness?: Politeness) => void
}

const AnnounceContext = createContext<AnnounceContextValue | null>(null)

export interface ScreenReaderAnnouncerProps {
  children: ReactNode
}

export function ScreenReaderAnnouncer({ children }: ScreenReaderAnnouncerProps) {
  const [politeAnnouncement, setPoliteAnnouncement] = useState('')
  const [assertiveAnnouncement, setAssertiveAnnouncement] = useState('')

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    if (politeness === 'assertive') {
      // Clear first, then set to ensure announcement
      setAssertiveAnnouncement('')
      setTimeout(() => setAssertiveAnnouncement(message), 50)
    } else {
      setPoliteAnnouncement('')
      setTimeout(() => setPoliteAnnouncement(message), 50)
    }
  }, [])

  return (
    <AnnounceContext.Provider value={{ announce }}>
      {children}

      {/* Polite announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeAnnouncement}
      </div>

      {/* Assertive announcements */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveAnnouncement}
      </div>
    </AnnounceContext.Provider>
  )
}

/**
 * useAnnounce Hook
 * Provides access to the screen reader announcer
 */
export function useAnnounce() {
  const context = useContext(AnnounceContext)

  if (!context) {
    // Return a no-op if used outside provider
    return {
      announce: (message: string, _politeness?: Politeness) => {
        console.warn(
          'useAnnounce: ScreenReaderAnnouncer not found. Message not announced:',
          message
        )
      },
    }
  }

  return context
}

/**
 * VisuallyHidden Component
 * Hides content visually but keeps it accessible to screen readers
 */
export interface VisuallyHiddenProps {
  children: ReactNode
  /** Make visible when focused (for skip links) */
  focusable?: boolean
}

export function VisuallyHidden({ children, focusable = false }: VisuallyHiddenProps) {
  return (
    <span className={focusable ? 'sr-only focus:not-sr-only' : 'sr-only'}>
      {children}
    </span>
  )
}
